import { useNavigate } from "@tanstack/react-router";
import {
  Ellipsis,
  FileCheck,
  Languages,
  type LucideIcon,
  Package,
  Pencil,
  Play,
  Rocket,
  Settings,
  Settings2,
  Trash,
  Wrench,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { track } from "../../analytics";
import { ConfirmationButton } from "../../ds/ConfirmationButton";
import { IconAction } from "../../ds/IconAction";
import { useRovingTabindex } from "../../ds/useRovingTabindex";
import {
  canDeleteEpisode,
  canEditEpisode,
  canOpenEditor,
  canPostProduce,
  canPostProduceVoices,
  canPublish,
  canValidateEpisode,
} from "../../permissions";
import type { RegistryEpisode, UserRole } from "../../registry";
import { useStateLS, useUser } from "../../utils";
import { makeBookUrl } from "../database";
import { EpisodeFormDialog } from "./EpisodeFormDialog";
import { useDeleteEpisode } from "./mutations";

// Max buttons shown directly in the group; anything beyond N-1 goes into the dropdown.
const MAX_TOOLBAR_ACTIONS = 6;

interface Action {
  key: string;
  tooltip: string;
  icon: LucideIcon;
  onClick?: () => void;
  href?: string;
  confirm?: boolean;
}

function ActionButton({
  action,
  tabIndex,
}: {
  action: Action;
  tabIndex?: number;
}) {
  const { tooltip, icon, onClick, href, confirm } = action;
  // `tooltip` becomes the accessible name as well as the visible tooltip, so
  // the two can't drift apart.
  return (
    <IconAction
      label={tooltip}
      icon={icon}
      href={href}
      as={confirm ? ConfirmationButton : undefined}
      tabIndex={tabIndex}
      variant="secondary"
      className="font-light"
      onClick={onClick}
    />
  );
}

interface EpisodeToolbarProps {
  chapter: RegistryEpisode;
  className?: string;
  userRole?: UserRole;
  /** Buttons shown before the rest collapses into the "More actions" dropdown.
   *  Page headers have room for everything (Infinity); episode cards keep the default. */
  maxActions?: number;
}

export default function EpisodeToolbar({
  chapter,
  className,
  userRole,
  maxActions = MAX_TOOLBAR_ACTIONS,
}: EpisodeToolbarProps) {
  const navigate = useNavigate();
  const weblateUrl = chapter.weblateLink
    ? `https://weblate.playnook.games/projects/${chapter.weblateLink}`
    : null;
  const { data: user } = useUser();
  const deleteEpisode = useDeleteEpisode();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [useExperimental] = useStateLS("useExperimental", false);
  const [pendingConfirmAction, setPendingConfirmAction] =
    useState<Action | null>(null);

  const actions: Action[] = [
    canOpenEditor(userRole) && {
      key: "editor",
      tooltip: "Open editor",
      icon: Pencil,
      href: makeBookUrl(
        chapter.pk,
        (user?.canUseExperimentalFeatures && useExperimental) ?? false,
      ),
      onClick: () =>
        track("episode_editor_opened", {
          episode_id: chapter.pk,
          series_id: chapter.series,
        }),
    },
    canEditEpisode(userRole) && {
      key: "edit",
      tooltip: "Edit details",
      icon: Settings2,
      onClick: () => setShowEditDialog(true),
    },
    canValidateEpisode(userRole) && {
      key: "validate",
      tooltip: "Validate",
      icon: FileCheck,
      onClick: () => {
        track("episode_validation_started", {
          episode_id: chapter.pk,
          series_id: chapter.series,
        });
        navigate({ to: `/validate/${chapter.pk}` });
      },
    },
    {
      key: "play",
      tooltip: "Play",
      icon: Play,
      onClick: () => {
        track("episode_playback_started", {
          episode_id: chapter.pk,
          series_id: chapter.series,
        });
        navigate({ to: `/play/${chapter.pk}` });
      },
    },
    canPostProduceVoices(userRole) && {
      key: "postprod",
      tooltip: "Post-production",
      icon: Package,
      onClick: () => navigate({ to: `/postprod/${chapter.pk}` }),
    },
    canPublish(userRole) && {
      key: "publish",
      tooltip: "Publishing",
      icon: Rocket,
      onClick: () => navigate({ to: `/publish/${chapter.pk}` }),
    },
    canPostProduce(userRole) && {
      key: "tasks",
      tooltip: "Tasks",
      icon: Wrench,
      onClick: () => navigate({ to: `/tasks/${chapter.pk}` }),
    },
    user?.isStaff &&
      weblateUrl && {
        key: "weblate",
        tooltip: "Translate",
        icon: Languages,
        href: weblateUrl,
      },
    user?.isStaff && {
      key: "admin",
      tooltip: "Admin settings",
      icon: Settings,
      href: `/admin/books/episode/${chapter.pk}/change/`,
    },
    canDeleteEpisode(userRole) && {
      key: "delete",
      tooltip: "Delete",
      icon: Trash,
      confirm: true,
      onClick: () =>
        deleteEpisode.mutate(chapter.pk, {
          onSuccess: () =>
            track("episode_deleted", {
              episode_id: chapter.pk,
              series_id: chapter.series,
            }),
        }),
    },
  ].filter(Boolean) as Action[];

  const hasOverflow = actions.length > maxActions;
  const visibleActions = hasOverflow
    ? actions.slice(0, maxActions - 1)
    : actions;
  const overflowActions = hasOverflow ? actions.slice(maxActions - 1) : [];

  // One tab stop for the whole toolbar, arrow keys within it. An episode list
  // renders one of these per card, so without it a reader is tabbing through
  // seven controls per episode to reach whatever they came for.
  const { ref, onKeyDown, tabIndexFor } = useRovingTabindex(
    visibleActions.length + (hasOverflow ? 1 : 0),
  );

  return (
    <>
      {pendingConfirmAction && (
        <Dialog open onOpenChange={() => setPendingConfirmAction(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Are you sure?</DialogTitle>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="destructive"
                onClick={() => {
                  pendingConfirmAction.onClick?.();
                  setPendingConfirmAction(null);
                }}
              >
                Confirm
              </Button>
              <Button
                variant="outline"
                onClick={() => setPendingConfirmAction(null)}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <ButtonGroup
        ref={ref}
        // `toolbar` rather than ButtonGroup's default `group`: this is a set of
        // controls navigated as one widget, which is what the role announces.
        role="toolbar"
        onKeyDown={onKeyDown}
        // flex-wrap so the group degrades gracefully at larger text sizes:
        // ConfirmationButton expands to a text label when armed, which would
        // otherwise overflow the card footer.
        className={cn("w-full flex-wrap *:flex-1", className)}
        aria-label="Episode actions"
      >
        {visibleActions.map((action, index) => (
          <ActionButton
            key={action.key}
            action={action}
            tabIndex={tabIndexFor(index)}
          />
        ))}

        {overflowActions.length > 0 && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    variant="secondary"
                    className="font-light cursor-pointer"
                    tabIndex={tabIndexFor(visibleActions.length)}
                    aria-label="More actions"
                  >
                    <Ellipsis className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>More actions</TooltipContent>
            </Tooltip>

            <DropdownMenuContent align="end">
              {overflowActions.map((action) =>
                action.href ? (
                  <DropdownMenuItem key={action.key} asChild>
                    <a
                      href={action.href}
                      target="_blank"
                      rel="noopener"
                      onClick={action.onClick}
                    >
                      <action.icon className="h-4 w-4 mr-2" />
                      {action.tooltip}
                    </a>
                  </DropdownMenuItem>
                ) : action.confirm ? (
                  <DropdownMenuItem
                    key={action.key}
                    className="text-destructive"
                    onClick={() => setPendingConfirmAction(action)}
                  >
                    <action.icon className="h-4 w-4 mr-2" />
                    {action.tooltip}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem key={action.key} onClick={action.onClick}>
                    <action.icon className="h-4 w-4 mr-2" />
                    {action.tooltip}
                  </DropdownMenuItem>
                ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </ButtonGroup>

      <EpisodeFormDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        seriesPk={chapter.series}
        episode={chapter}
      />
    </>
  );
}
